/**
 * CapacidadeService — Motor de Inteligência Temporal V1
 *
 * Calcula a Disponibilidade Líquida (DR) de um usuário/cargo.
 *
 * Fórmula:
 *   DR = FTE_base × availability_pct × (1 - wave_impact_pct/100)
 *   Carga = Σ horas_estimadas(atividades_ativas)
 *   Ocupação = Carga / DR
 *
 * Regras:
 *   - Nunca chamar Supabase diretamente em componentes
 *   - Sempre filtrar por organization_id
 *   - T-Shirt sizing por padrão: P=2h M=8h G=24h GG=40h
 */

import { supabase } from '../lib/supabaseClient';

// ── Types ────────────────────────────────────────────────────

export interface CargoConfig {
  id: string;
  organizationId: string;
  name: string;
  fteBase: number;
  availabilityPct: number;
  wipMax: number;
  sizingHours: SizingMap;
  /** Custo por hora em R$ — Motor de Impacto Financeiro */
  custoHora: number;
  notes?: string | null;
}

export interface SizingMap {
  P: number;
  M: number;
  G: number;
  GG: number;
}

export const DEFAULT_SIZING: SizingMap = { P: 2, M: 8, G: 24, GG: 40 };
// Hours per week in a full-time position
const FTE_HOURS_WEEK = 40;

export interface CargoWeekBlock {
  id: string;
  cargoId: string;
  dayOfWeek: 0 | 1 | 2 | 3 | 4;
  block: 1 | 2 | 3 | 4;
  activityType: 'core' | 'gestao' | 'externo' | 'buffer';
  durationMinutes: number;
}

export interface CargoMonthlyWave {
  id: string;
  cargoId: string;
  name: string;
  dayStart: number;
  dayEnd: number;
  impactPct: number;
  affectedMonths: number[] | null;
}

export interface UserCargoAssignment {
  userId: string;
  cargoId: string;
  cargoName: string;
  fteBase: number;
  availabilityPct: number;
  wipMax: number;
  sizingHours: SizingMap;
}

export type TShirtSize = 'P' | 'M' | 'G' | 'GG';

export interface ActiveWorkItem {
  id: string;
  source: 'workflow' | 'action' | 'problem' | 'monitoring';
  estimatedHours?: number | null;
  size?: TShirtSize | null;
  priority?: string | null;
}

export interface UserCapacidadeResult {
  userId: string;
  cargoName: string | null;
  /** Disponibilidade Líquida em horas/semana */
  drHoursPerWeek: number;
  /** Carga total estimada em horas */
  currentLoadHours: number;
  /** Ocupação % = currentLoadHours / drHoursPerWeek * 100 */
  occupationPct: number;
  /** WIP atual (contagem de itens ativos) */
  activeItemCount: number;
  /** WIP máximo do cargo */
  wipMax: number;
  /** true se activeItemCount >= wipMax */
  isOverloaded: boolean;
  /** Onda mensal ativa (se houver) */
  activeWave: string | null;
}

// ── Helpers ──────────────────────────────────────────────────

function mapCargoRow(row: any): CargoConfig {
  return {
    id: row.id,
    organizationId: row.organization_id,
    name: row.name,
    fteBase: Number(row.fte_base ?? 1),
    availabilityPct: Number(row.availability_pct ?? 80),
    wipMax: Number(row.wip_max ?? 8),
    sizingHours: { ...DEFAULT_SIZING, ...(row.sizing_hours ?? {}) },
    custoHora: Number(row.custo_hora ?? 0),
    notes: row.notes ?? null,
  };
}

function mapWaveRow(row: any): CargoMonthlyWave {
  return {
    id: row.id,
    cargoId: row.cargo_id,
    name: row.name,
    dayStart: Number(row.day_start),
    dayEnd: Number(row.day_end),
    impactPct: Number(row.impact_pct ?? 0),
    affectedMonths: row.affected_months ?? null,
  };
}

function mapBlockRow(row: any): CargoWeekBlock {
  return {
    id: row.id,
    cargoId: row.cargo_id,
    dayOfWeek: Number(row.day_of_week) as 0 | 1 | 2 | 3 | 4,
    block: Number(row.block) as 1 | 2 | 3 | 4,
    activityType: row.activity_type,
    durationMinutes: Number(row.duration_minutes ?? 120),
  };
}

/** Resolve T-Shirt size → hours using cargo sizing map */
export function resolveSizingHours(
  size: TShirtSize | string | null | undefined,
  sizing: SizingMap = DEFAULT_SIZING,
): number {
  if (!size) return sizing.M; // fallback to medium
  const key = size.toUpperCase() as TShirtSize;
  return sizing[key] ?? sizing.M;
}

/** Active monthly wave impact for a given date */
function resolveWaveImpact(
  waves: CargoMonthlyWave[],
  date: Date = new Date(),
): { impactPct: number; name: string | null } {
  const day = date.getDate();
  const month = date.getMonth() + 1; // 1-based

  for (const wave of waves) {
    const inRange = day >= wave.dayStart && day <= wave.dayEnd;
    const inMonth = !wave.affectedMonths || wave.affectedMonths.includes(month);
    if (inRange && inMonth) {
      return { impactPct: wave.impactPct, name: wave.name };
    }
  }
  return { impactPct: 0, name: null };
}

/** Compute DR in hours/week */
export function computeDR(
  fteBase: number,
  availabilityPct: number,
  waveImpactPct: number,
): number {
  const rawHours = fteBase * FTE_HOURS_WEEK;
  const afterAvailability = rawHours * (availabilityPct / 100);
  const afterWave = afterAvailability * (1 - waveImpactPct / 100);
  return Math.max(0, Math.round(afterWave * 10) / 10);
}

// ── CRUD ─────────────────────────────────────────────────────

export async function listCargos(organizationId: string): Promise<CargoConfig[]> {
  if (!organizationId) return [];
  const { data, error } = await supabase
    .from('cargo_config')
    .select('*')
    .eq('organization_id', organizationId)
    .order('name');
  if (error) throw error;
  return (data ?? []).map(mapCargoRow);
}

export async function createCargo(
  organizationId: string,
  input: Omit<CargoConfig, 'id' | 'organizationId'>,
): Promise<CargoConfig> {
  const { data, error } = await supabase
    .from('cargo_config')
    .insert({
      organization_id: organizationId,
      name: input.name,
      fte_base: input.fteBase,
      availability_pct: input.availabilityPct,
      wip_max: input.wipMax,
      sizing_hours: input.sizingHours,
      notes: input.notes ?? null,
    })
    .select()
    .single();
  if (error) throw error;
  return mapCargoRow(data);
}

export async function updateCargo(
  cargoId: string,
  organizationId: string,
  patch: Partial<Omit<CargoConfig, 'id' | 'organizationId'>>,
): Promise<void> {
  const update: Record<string, unknown> = {};
  if (patch.name !== undefined) update.name = patch.name;
  if (patch.fteBase !== undefined) update.fte_base = patch.fteBase;
  if (patch.availabilityPct !== undefined) update.availability_pct = patch.availabilityPct;
  if (patch.wipMax !== undefined) update.wip_max = patch.wipMax;
  if (patch.sizingHours !== undefined) update.sizing_hours = patch.sizingHours;
  if (patch.custoHora !== undefined) update.custo_hora = patch.custoHora;
  if (patch.notes !== undefined) update.notes = patch.notes;

  const { error } = await supabase
    .from('cargo_config')
    .update(update)
    .eq('id', cargoId)
    .eq('organization_id', organizationId);
  if (error) throw error;
}

export async function deleteCargo(cargoId: string, organizationId: string): Promise<void> {
  const { error } = await supabase
    .from('cargo_config')
    .delete()
    .eq('id', cargoId)
    .eq('organization_id', organizationId);
  if (error) throw error;
}

export async function getCargoWeekBlocks(cargoId: string): Promise<CargoWeekBlock[]> {
  const { data, error } = await supabase
    .from('cargo_standard_week')
    .select('*')
    .eq('cargo_id', cargoId)
    .order('day_of_week')
    .order('block');
  if (error) throw error;
  return (data ?? []).map(mapBlockRow);
}

export async function upsertCargoWeekBlock(
  block: Omit<CargoWeekBlock, 'id'> & { organizationId: string },
): Promise<void> {
  const { error } = await supabase
    .from('cargo_standard_week')
    .upsert({
      cargo_id: block.cargoId,
      organization_id: block.organizationId,
      day_of_week: block.dayOfWeek,
      block: block.block,
      activity_type: block.activityType,
      duration_minutes: block.durationMinutes,
    }, { onConflict: 'cargo_id,day_of_week,block' });
  if (error) throw error;
}

export async function listCargoWaves(cargoId: string): Promise<CargoMonthlyWave[]> {
  const { data, error } = await supabase
    .from('cargo_monthly_waves')
    .select('*')
    .eq('cargo_id', cargoId)
    .order('day_start');
  if (error) throw error;
  return (data ?? []).map(mapWaveRow);
}

export async function getUserCargoAssignment(
  organizationId: string,
  userId: string,
): Promise<UserCargoAssignment | null> {
  const { data, error } = await supabase
    .from('user_cargo_assignment')
    .select('user_id, cargo_id, cargo_config(name, fte_base, availability_pct, wip_max, sizing_hours)')
    .eq('organization_id', organizationId)
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  const c = (data as any).cargo_config;
  return {
    userId: data.user_id,
    cargoId: data.cargo_id,
    cargoName: c?.name ?? null,
    fteBase: Number(c?.fte_base ?? 1),
    availabilityPct: Number(c?.availability_pct ?? 80),
    wipMax: Number(c?.wip_max ?? 8),
    sizingHours: { ...DEFAULT_SIZING, ...(c?.sizing_hours ?? {}) },
  };
}

export async function assignUserToCargo(
  organizationId: string,
  userId: string,
  cargoId: string,
): Promise<void> {
  const { error } = await supabase
    .from('user_cargo_assignment')
    .upsert(
      { organization_id: organizationId, user_id: userId, cargo_id: cargoId },
      { onConflict: 'organization_id,user_id' },
    );
  if (error) throw error;
}

/**
 * Retorna mapa userId → wip_max para todos os usuários de uma org.
 * Usado pelo Poka-Yoke de sobrecarga em TeamPage.
 */
export async function getOrgWipMaxByUser(
  organizationId: string,
): Promise<Map<string, number>> {
  const { data, error } = await supabase
    .from('user_cargo_assignment')
    .select('user_id, cargo_config(wip_max)')
    .eq('organization_id', organizationId);
  if (error) throw error;

  const map = new Map<string, number>();
  for (const row of data ?? []) {
    const wipMax = (row as any).cargo_config?.wip_max;
    if (row.user_id && wipMax != null) {
      map.set(row.user_id as string, Number(wipMax));
    }
  }
  return map;
}

export async function getOrgCargoNameByUser(
  organizationId: string,
): Promise<Map<string, string>> {
  const { data, error } = await supabase
    .from('user_cargo_assignment')
    .select('user_id, cargo_config(name)')
    .eq('organization_id', organizationId);
  if (error) throw error;

  const map = new Map<string, string>();
  for (const row of data ?? []) {
    const name = (row as any).cargo_config?.name;
    if (row.user_id && name) {
      map.set(row.user_id as string, String(name));
    }
  }
  return map;
}

// ── Core Calculation ─────────────────────────────────────────

/**
 * Calcula a Disponibilidade Líquida e Carga atual de um usuário.
 *
 * @param organizationId
 * @param userId
 * @param activeItems - Lista de atividades ativas do usuário
 * @param referenceDate - Data de referência para ondas mensais (default: today)
 */
export async function computeUserCapacidade(
  organizationId: string,
  userId: string,
  activeItems: ActiveWorkItem[],
  referenceDate: Date = new Date(),
): Promise<UserCapacidadeResult> {
  // Fallback defaults when no cargo is assigned
  const DEFAULTS: UserCargoAssignment = {
    userId,
    cargoId: '',
    cargoName: null,
    fteBase: 1,
    availabilityPct: 80,
    wipMax: 8,
    sizingHours: DEFAULT_SIZING,
  };

  const assignment = await getUserCargoAssignment(organizationId, userId) ?? DEFAULTS;
  const waves = assignment.cargoId
    ? await listCargoWaves(assignment.cargoId)
    : [];

  const { impactPct, name: waveName } = resolveWaveImpact(waves, referenceDate);
  const drHoursPerWeek = computeDR(assignment.fteBase, assignment.availabilityPct, impactPct);

  // Estimate load in hours
  const currentLoadHours = activeItems.reduce((sum, item) => {
    if (item.estimatedHours != null) return sum + item.estimatedHours;
    return sum + resolveSizingHours(item.size, assignment.sizingHours);
  }, 0);

  const occupationPct = drHoursPerWeek > 0
    ? Math.round((currentLoadHours / drHoursPerWeek) * 1000) / 10
    : 0;

  const activeItemCount = activeItems.length;
  const isOverloaded = activeItemCount >= assignment.wipMax;

  return {
    userId,
    cargoName: assignment.cargoName,
    drHoursPerWeek,
    currentLoadHours,
    occupationPct,
    activeItemCount,
    wipMax: assignment.wipMax,
    isOverloaded,
    activeWave: waveName,
  };
}

// ── Process Mining ───────────────────────────────────────────

/**
 * Grava uma transição de status na task_time_logs (Process Mining).
 * Fire-and-forget — não lança para o caller em caso de erro não crítico.
 */
export async function logTaskTransition(params: {
  organizationId: string;
  taskId: string;
  source: 'workflow' | 'action' | 'problem' | 'monitoring';
  userId: string | null | undefined;
  statusFrom: string;
  statusTo: string;
  previousTransitionAt?: string | null;
  metadata?: Record<string, unknown>;
}): Promise<void> {
  const durationSeconds = params.previousTransitionAt
    ? Math.round(
        (Date.now() - new Date(params.previousTransitionAt).getTime()) / 1000,
      )
    : null;

  const { error } = await supabase.from('task_time_logs').insert({
    organization_id: params.organizationId,
    task_id: params.taskId,
    source: params.source,
    user_id: params.userId ?? null,
    status_from: params.statusFrom,
    status_to: params.statusTo,
    duration_seconds: durationSeconds,
    metadata: params.metadata ?? null,
  });

  if (error) {
    // Non-critical: log silently, do not interrupt the caller flow
    console.warn('[CapacidadeService] logTaskTransition error (non-critical):', error.message);
  }
}

// ── Analytics ────────────────────────────────────────────────

export interface ProcessMiningMetrics {
  taskId: string;
  source: string;
  avgProcessingSeconds: number | null;
  avgWaitSeconds: number | null;
  transitionCount: number;
}

/**
 * Calcula métricas de Process Mining para um conjunto de tasks.
 * Processing time = duração da transição para_started → para_completed
 * Wait time = duração da transição para_pending → para_started
 */
export async function getProcessMiningMetrics(
  organizationId: string,
  taskIds: string[],
): Promise<ProcessMiningMetrics[]> {
  if (!taskIds.length) return [];

  const { data, error } = await supabase
    .from('task_time_logs')
    .select('task_id, source, status_from, status_to, duration_seconds, created_at')
    .eq('organization_id', organizationId)
    .in('task_id', taskIds)
    .order('created_at');
  if (error) throw error;

  const byTask = new Map<string, typeof data>();
  for (const row of data ?? []) {
    const key = row.task_id as string;
    if (!byTask.has(key)) byTask.set(key, []);
    byTask.get(key)!.push(row);
  }

  return Array.from(byTask.entries()).map(([taskId, rows]) => {
    const processingRows = rows.filter(
      r => r.status_from === 'in_progress' && r.status_to === 'completed',
    );
    const waitRows = rows.filter(
      r => r.status_from === 'pending' && r.status_to === 'in_progress',
    );

    const avg = (arr: typeof rows) =>
      arr.length
        ? Math.round(
            arr.reduce((s, r) => s + (r.duration_seconds ?? 0), 0) / arr.length,
          )
        : null;

    return {
      taskId,
      source: rows[0]?.source ?? 'workflow',
      avgProcessingSeconds: avg(processingRows),
      avgWaitSeconds: avg(waitRows),
      transitionCount: rows.length,
    };
  });
}

// ── BI Functions (V18) ───────────────────────────────────────

export interface DailyTrendPoint {
  date: string;   // YYYY-MM-DD
  label: string;  // dd/MM
  value: number;
}

const ptDay = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' });

function buildDayRange(days: number): DailyTrendPoint[] {
  const today = new Date();
  return Array.from({ length: days }, (_, i) => {
    const d = new Date(today);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - (days - 1 - i));
    return { date: d.toISOString().slice(0, 10), label: ptDay.format(d), value: 0 };
  });
}

/**
 * Lead Time médio por dia baseado nos logs reais de Process Mining.
 * Lead Time = duração da transição in_progress → completed (segundos → horas).
 */
export async function getLeadTimeTrend(
  organizationId: string,
  days: number = 14,
): Promise<DailyTrendPoint[]> {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);

  const { data, error } = await supabase
    .from('task_time_logs')
    .select('created_at, duration_seconds')
    .eq('organization_id', organizationId)
    .eq('status_from', 'in_progress')
    .eq('status_to', 'completed')
    .not('duration_seconds', 'is', null)
    .gte('created_at', cutoff.toISOString())
    .order('created_at');
  if (error) throw error;

  const buckets = new Map<string, number[]>();
  (data ?? []).forEach(row => {
    const day = (row.created_at as string).slice(0, 10);
    const h = (row.duration_seconds as number) / 3600;
    if (!buckets.has(day)) buckets.set(day, []);
    buckets.get(day)!.push(h);
  });

  return buildDayRange(days).map(p => ({
    ...p,
    value: buckets.has(p.date)
      ? Math.round((buckets.get(p.date)!.reduce((s, v) => s + v, 0) / buckets.get(p.date)!.length) * 10) / 10
      : 0,
  }));
}

/**
 * Throughput por dia: contagem de tarefas completadas baseada em task_time_logs.
 */
export async function getThroughputTrend(
  organizationId: string,
  days: number = 14,
): Promise<DailyTrendPoint[]> {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);

  const { data, error } = await supabase
    .from('task_time_logs')
    .select('created_at')
    .eq('organization_id', organizationId)
    .eq('status_to', 'completed')
    .gte('created_at', cutoff.toISOString())
    .order('created_at');
  if (error) throw error;

  const counts = new Map<string, number>();
  (data ?? []).forEach(row => {
    const day = (row.created_at as string).slice(0, 10);
    counts.set(day, (counts.get(day) ?? 0) + 1);
  });

  return buildDayRange(days).map(p => ({
    ...p,
    value: counts.get(p.date) ?? 0,
  }));
}

export interface StageWipRow {
  stageId: string;
  stageName: string;
  wipAtual: number;
  wipLimite: number | null;
  overLimit: boolean;
}

/**
 * WIP por etapa para o Heatmap de Gargalos.
 * Cruza tarefas ativas com wip_limite configurado em workflow_stages_v9.
 */
export async function getStageWipHeatmap(
  organizationId: string,
  templateId?: string,
): Promise<StageWipRow[]> {
  let taskQuery = supabase
    .from('workflow_tasks_v9')
    .select('stage_id, stage:workflow_stages_v9(name, wip_limite)')
    .eq('organization_id', organizationId)
    .in('status', ['pending', 'in_progress']);
  if (templateId) {
    taskQuery = taskQuery.eq('template_id', templateId);
  }
  const { data, error } = await taskQuery;
  if (error) throw error;

  const stageMap = new Map<string, StageWipRow>();
  (data ?? []).forEach((row: any) => {
    const id = row.stage_id as string;
    if (!id) return;
    const stage = row.stage as { name?: string; wip_limite?: number | null } | null;
    if (!stageMap.has(id)) {
      stageMap.set(id, {
        stageId: id,
        stageName: stage?.name ?? id,
        wipAtual: 0,
        wipLimite: stage?.wip_limite ?? null,
        overLimit: false,
      });
    }
    const s = stageMap.get(id)!;
    s.wipAtual += 1;
    if (s.wipLimite != null) s.overLimit = s.wipAtual > s.wipLimite;
  });

  return [...stageMap.values()].sort((a, b) => b.wipAtual - a.wipAtual);
}

export interface FinancialImpactPoint {
  date: string;
  label: string;
  /** Economia acumulada no dia em R$ */
  economia: number;
  /** Custo extra (desvio negativo) no dia em R$ */
  custo: number;
}

/**
 * Impacto Financeiro diário.
 * Economia = Σ ( sizing_hours - real_hours ) × custo_hora_cargo  — quando real < sizing.
 * Custo extra = Σ ( real_hours - sizing_hours ) × custo_hora_cargo — quando real > sizing.
 */
export async function getFinancialImpactTrend(
  organizationId: string,
  days: number = 14,
): Promise<FinancialImpactPoint[]> {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);

  // Fetch completed transitions with duration and context
  const { data: logs, error } = await supabase
    .from('task_time_logs')
    .select('created_at, duration_seconds, user_id, metadata')
    .eq('organization_id', organizationId)
    .eq('status_from', 'in_progress')
    .eq('status_to', 'completed')
    .not('duration_seconds', 'is', null)
    .gte('created_at', cutoff.toISOString());
  if (error) throw error;

  // Fetch all user→cargo assignments for the org
  const { data: assignments } = await supabase
    .from('user_cargo_assignment')
    .select('user_id, cargo_config(custo_hora, sizing_hours)')
    .eq('organization_id', organizationId);

  const cargoByUser = new Map<string, { custoHora: number; sizingHours: SizingMap }>();
  (assignments ?? []).forEach((row: any) => {
    const c = row.cargo_config;
    if (c && row.user_id) {
      cargoByUser.set(row.user_id as string, {
        custoHora: Number(c.custo_hora ?? 0),
        sizingHours: { ...DEFAULT_SIZING, ...(c.sizing_hours ?? {}) },
      });
    }
  });

  // Accumulate per day
  const buckets = new Map<string, { economia: number; custo: number }>();

  (logs ?? []).forEach((row: any) => {
    const day = (row.created_at as string).slice(0, 10);
    const cargo = cargoByUser.get(row.user_id as string);
    if (!cargo || cargo.custoHora <= 0) return;

    const realHours = (row.duration_seconds as number) / 3600;
    // Try to get size from metadata
    const meta = row.metadata as Record<string, unknown> | null;
    const size = meta?.size as string | undefined;
    const standardHours = resolveSizingHours(size, cargo.sizingHours);
    const diff = standardHours - realHours; // positive = economy

    if (!buckets.has(day)) buckets.set(day, { economia: 0, custo: 0 });
    const b = buckets.get(day)!;
    const value = Math.abs(diff) * cargo.custoHora;
    if (diff > 0) b.economia += value;
    else b.custo += value;
  });

  return buildDayRange(days).map(p => {
    const b = buckets.get(p.date) ?? { economia: 0, custo: 0 };
    return {
      date: p.date,
      label: p.label,
      economia: Math.round(b.economia * 100) / 100,
      custo: Math.round(b.custo * 100) / 100,
    };
  });
}

/** Cargo config com custo_hora incluído */
export async function updateCargoCustoHora(
  cargoId: string,
  organizationId: string,
  custoHora: number,
): Promise<void> {
  const { error } = await supabase
    .from('cargo_config')
    .update({ custo_hora: custoHora })
    .eq('id', cargoId)
    .eq('organization_id', organizationId);
  if (error) throw error;
}

